/**
 * Core data model for the room-perimeter geometry engine.
 *
 * Pure, DOM-free, framework-free (shareable web + native). The footprint is a
 * single rectangle whose four corners may be chamfered and whose four edges
 * may carry a sequence of outward alcoves. Because alcoves are
 * always outward and their along-edge spans never overlap, the resolved
 * boundary is always a simple polygon: no 2D overlap resolution needed.
 *
 * Lengths are in feet; areas in ft².
 *
 * **Zod is the single source of truth here:** every shape is defined as a Zod
 * schema so the runtime validator and the compile-time type can never drift, and
 * pasted / imported JSON can be validated (with field-level error messages)
 * against the exact same definition the rest of the codebase types against.
 *
 * Each shape's type is also declared as an explicit interface beside its
 * schema, kept in sync by hand: the inferred types sit at TypeScript's
 * instantiation-depth limit (see the `FloorPlanRoom` note). Everything stays a
 * pure data object (no methods, no getters), so a `FloorPlanRoom` is directly
 * JSON-serializable.
 */

import { number, optionalBoolean, optionalNumber, optionalString, z, zenum, zodLess, ZodLessInfer } from '@kaiizen/zod-less';

/**
 * A point in a room's plan, in feet: x along the back edge, y toward the
 * front. The raw-scan intake works in the same shape, in meters
 * (`RawFloorPlanSchema`'s points) and then in feet with y up.
 */
export interface Point {
  x: number;
  y: number;
}

/** Edge property names: compass directions matching FloorPlanRoom properties. */
export const EdgeNameSchema = zenum('leftEdge', 'backEdge', 'rightEdge', 'frontEdge');
export const EDGE_NAMES = EdgeNameSchema.options;
export type EdgeName = ZodLessInfer<typeof EdgeNameSchema>;

export const AlcoveEdgeNameSchema = zenum('leftEdge', 'backEdge', 'rightEdge');
export const ALCOVE_EDGE_NAMES = AlcoveEdgeNameSchema.options;
export type AlcoveEdgeName = ZodLessInfer<typeof AlcoveEdgeNameSchema>;

/** Corner property names: compass directions matching FloorPlanRoom properties. */
export const CornerNameSchema = zenum('backLeftCorner', 'backRightCorner', 'frontRightCorner', 'frontLeftCorner');
export const CORNER_NAMES = CornerNameSchema.options;
export type CornerName = ZodLessInfer<typeof CornerNameSchema>;

/** An alcove's two outer (back) corners: the only ones that carry a chamfer. */
export const OuterWhichSchema = zenum('backLeftCorner', 'backRightCorner');
export const OUTER_WHICH = OuterWhichSchema.options;
export type OuterWhich = ZodLessInfer<typeof OuterWhichSchema>;

/** A free-floating interior area (secondary geometry). `object` is a free 3-D box
 *  (furniture / appliances / cabinets / fixtures); the others are flat regions. */
export const CustomAreaTypeSchema = zenum('flooring', 'counter', 'wall', 'object');
export const CUSTOM_AREA_TYPES = CustomAreaTypeSchema.options;
export type CustomAreaType = ZodLessInfer<typeof CustomAreaTypeSchema>;

/** Object subtype: what an `object` area represents. Drives default rendering. */
export const ObjectSubtypeSchema = zenum('cabinets', 'furniture', 'appliance', 'bathtub', 'shower', 'toilet', 'sink', 'electronics');
export const OBJECT_SUBTYPES = ObjectSubtypeSchema.options;
export type ObjectSubtype = ZodLessInfer<typeof ObjectSubtypeSchema>;

/** What an edge item is. Only "alcove" changes the footprint polygon; doors,
 *  openings, and windows are flat-wall features that cut the wall. */
export const EdgeSegmentTypeSchema = zenum('alcove', 'door', 'opening', 'window');
export const EDGE_SEGMENT_TYPES = EdgeSegmentTypeSchema.options;
export type EdgeSegmentType = ZodLessInfer<typeof EdgeSegmentTypeSchema>;

/** Door configuration (kind === "door"): hinged singles/pairs, pockets, plus
 *  closet & garage types: "bifold" (4 panels hinged in folding pairs),
 *  "bypass" (two sliding panels on parallel tracks), "garage" (overhead door);
 *  and sliding glass doors ("slideGlassLeft"/"slideGlassRight": two glazed
 *  panels, named for which side the sliding panel is on; pocket-like in plan). */
export const DoorSwingSchema = zenum(
  'inLeft',
  'inRight',
  'outLeft',
  'outRight',
  'pairIn',
  'pairOut',
  'pocketLeft',
  'pocketRight',
  'bifold',
  'bypass',
  'garage',
  'slideGlassLeft',
  'slideGlassRight',
);
export const DOOR_SWINGS = DoorSwingSchema.options;
export type DoorSwing = ZodLessInfer<typeof DoorSwingSchema>;

/** A type guard for one of a closed set of known string values. */
const isOneOf = <Value extends string>(values: readonly Value[]) => (value: string): value is Value => (values as readonly string[]).includes(value);

/** True for the edge segment types this version knows. Anything else came from a newer version. */
export const isKnownEdgeSegmentType = isOneOf(EDGE_SEGMENT_TYPES);

/** True for the door swings this version knows. Anything else came from a newer version. */
export const isKnownDoorSwing = isOneOf(DOOR_SWINGS);

/**
 * A string field whose known values are `knownValues`, but which accepts any
 * string, so a value a newer version adds passes validation and is saved back
 * as written. Typed as the known values: code here is written against the
 * values it knows, and checks the matching `isKnown...` guard before relying on
 * one (an unknown value draws and measures as the plain thing it most
 * resembles).
 */
const extensible = <KnownValues extends z.ZodTypeAny>(knownValues: KnownValues): KnownValues => z.union([knownValues, z.string()]) as unknown as KnownValues;

/**
 * An object schema that keeps the fields it doesn't know instead of stripping
 * them, so a field a newer version adds passes validation (the server's
 * included) and is saved back as written. Typed as the known fields, like
 * `extensible`: code here is written against the fields it knows.
 */
const keepingUnknownFields = <Schema extends { loose: () => unknown }>(schema: Schema): Schema => schema.loose() as Schema;

export const FloorPlanEdgeSegmentSimpleSchema = keepingUnknownFields(zodLess({
  name: optionalString,

  edgeSegmentType: extensible(EdgeSegmentTypeSchema),
  position: number.describe('distance from the left edge of the segment'),
  width: number.describe('width of the segment'),
  openingBase: optionalNumber.describe('Bottom of the opening above the floor. Undefined === starts at the floor'),
  openingHeight: optionalNumber.describe('Height of the opening above openingBase. Undefined === full height to ceiling.'),

  swing: extensible(DoorSwingSchema).optional().describe('door type'),
}));
/** Explicit interface (see FloorPlanRoom note): keep in sync with the schema above. */
export interface FloorPlanEdgeSegmentSimple {
  name?: string;
  edgeSegmentType: EdgeSegmentType;
  /** Distance from the left edge of the segment. */
  position: number;
  width: number;
  /** Bottom of the opening above the floor. Undefined === starts at the floor. */
  openingBase?: number;
  /** Height of the opening above openingBase. Undefined === full height to ceiling. */
  openingHeight?: number;
  swing?: DoorSwing;
}
/**
 * A corner of a room or alcove: chamfer geometry plus optional edge items
 * along the diagonal cut. Segments are positioned along the diagonal (0 →
 * length of diagonal). Only door/opening/window are valid here (no alcoves).
 */
export const FloorPlanCornerSchema = keepingUnknownFields(zodLess({
  dx: number,
  dy: number,
  segments: zodLess([FloorPlanEdgeSegmentSimpleSchema]).optional(),
}));
/** Explicit interface (see FloorPlanRoom note): keep in sync with the schema above. */
export interface FloorPlanCorner {
  dx: number;
  dy: number;
  segments?: FloorPlanEdgeSegmentSimple[];
}

/**
 * An edge surface on a non-primary wall: an alcove wall or a corner
 * `segments` are the edge items (door/opening/window -- but not alcove) on this surface.
 */
export const FloorPlanEdgeSimpleSchema = keepingUnknownFields(z.object({
  segments: zodLess([FloorPlanEdgeSegmentSimpleSchema]),
}));
/** Explicit interface (see FloorPlanRoom note): keep in sync with the schema above. */
export interface FloorPlanEdgeSimple {
  segments: FloorPlanEdgeSegmentSimple[];
}

/**
 * An outward alcove attached to a parent edge, or a flat-wall feature
 * (door/opening/window).
 *
 * `position`/`width` describe the footprint span along the edge (measured from
 * the edge's start corner). The two OUTER (back) corners carry chamfer geometry
 * (dx/dy). Left/back/right are the three wall surfaces of the alcove, each an
 * embedded FloorPlanEdge.
 *
 * Note: alcoves have **no inner (front/mouth) corners**. A widened/flared mouth
 * is expressed by abutting a second alcove on the same edge: touching alcoves
 * merge (their shared wall dissolves), which covers the same layouts without the
 * special-case flare geometry.
 */
export const FloorPlanEdgeSegmentSchema = FloorPlanEdgeSegmentSimpleSchema.extend({
  /** Alcoves: the two OUTER (back) corner chamfers, in boundary-walk order.
   *  Undefined === square corner (doors/windows/openings never carry them). */
  backLeftCorner: FloorPlanCornerSchema.optional(),
  backRightCorner: FloorPlanCornerSchema.optional(),

  /** Alcoves: depth (how far back the alcove extends from the room's edge */
  depth: optionalNumber,
  /** Alcoves: Left wall (as seen facing the alcove from the center of the room) */
  leftEdge: FloorPlanEdgeSimpleSchema.optional(),
  /** Alcoves: Back wall (as seen facing the alcove from the center of the room) */
  backEdge: FloorPlanEdgeSimpleSchema.optional(),
  /** Alcoves: Right wall (as seen facing the alcove from the center of the room) */
  rightEdge: FloorPlanEdgeSimpleSchema.optional(),
});
/** Explicit interface (see FloorPlanRoom note): keep in sync with the schema above. */
export interface FloorPlanEdgeSegment extends FloorPlanEdgeSegmentSimple {
  /** Alcoves: the two OUTER (back) corner chamfers. Undefined === square corner. */
  backLeftCorner?: FloorPlanCorner;
  backRightCorner?: FloorPlanCorner;
  /** Alcoves: depth (how far back the alcove extends from the room's edge). */
  depth?: number;
  /** Alcoves: the three wall surfaces, as seen facing the alcove from the room. */
  leftEdge?: FloorPlanEdgeSimple;
  backEdge?: FloorPlanEdgeSimple;
  rightEdge?: FloorPlanEdgeSimple;
}

// **************************************************************************************
// Free elements: flooring, counters, walls, objects
// Any length x width x height(optional) shape that can be places arbitrarily in the room.
// **************************************************************************************
/**
 * An edge surface: a primary wall, an alcove wall, etc.
 * `segments` are the edge items (door/opening/window/alcove) on this surface.
 */
export const FloorPlanPrimaryEdgeSchema = keepingUnknownFields(z.object({
  segments: zodLess([FloorPlanEdgeSegmentSchema]),
}));
/** Explicit interface (see FloorPlanRoom note): keep in sync with the schema above. */
export interface FloorPlanPrimaryEdge {
  segments: FloorPlanEdgeSegment[];
}

const FloorPlanFreeElementBaseSchema = keepingUnknownFields(z.object({
  name: optionalString.describe('name of the area'),
  x: number,
  y: number,
  width: number,
  length: number,
  height: optionalNumber.describe('height of the area'),
  base: optionalNumber.describe('distance from the floor to the bottom of the area. Undefined === on the floor.'),
  rotation: optionalNumber.describe("Rotation about the rect's center, in DEGREES as seen in the 2D plan. Undefined === 0 (axis-aligned)."),

  /** Corners (clockwise: backLeftCorner, backRightCorner, frontRightCorner,
   *  frontLeftCorner). Missing === square corner. */
  backLeftCorner: FloorPlanCornerSchema.optional(),
  backRightCorner: FloorPlanCornerSchema.optional(),
  frontRightCorner: FloorPlanCornerSchema.optional(),
  frontLeftCorner: FloorPlanCornerSchema.optional(),
}));

const FlooringElementSchema = FloorPlanFreeElementBaseSchema.extend({ customAreaType: zodLess('flooring'), floorType: optionalString });
const CounterElementSchema = FloorPlanFreeElementBaseSchema.extend({ customAreaType: zodLess('counter') });
const WallElementSchema = FloorPlanFreeElementBaseSchema.extend({ customAreaType: zodLess('wall') });

/**
 * A free 3-D box: furniture, appliances, cabinets, a wall-mounted AC unit, etc.
 * Spans [base, base + height], so it can float in midair (a wall cabinet, an AC
 * high on the wall). The three fields here are object-only by construction.
 */
export const ObjectElementSchema = FloorPlanFreeElementBaseSchema.extend({
  customAreaType: z.literal('object'),
  notBuiltIn: optionalBoolean.describe(
    'true for movable furniture / appliances; false or undefined = built-in (e.g. cabinets, fixtures, bathtub, etc.).',
  ),
  subtype: extensible(ObjectSubtypeSchema).optional().describe('what the object is (cabinets / furniture / appliances).'),
});

const KnownFreeElementSchema = z.discriminatedUnion('customAreaType', [
  FlooringElementSchema,
  CounterElementSchema,
  WallElementSchema,
  ObjectElementSchema,
]);
export type FloorPlanFreeElement = ZodLessInfer<typeof KnownFreeElementSchema>;

/** True for the element types this version knows. Anything else came from a newer version. */
export const isKnownCustomAreaType = isOneOf(CUSTOM_AREA_TYPES);

/**
 * An element type this version doesn't know, written by a newer one. It still
 * has the shared box fields, so it parses, draws and moves as a plain box, and
 * a save keeps it instead of rejecting the whole floor plan.
 */
const UnknownFreeElementSchema = FloorPlanFreeElementBaseSchema.extend({ customAreaType: z.string() });

/** The schema an element must match: its own type's when this version knows the type, the plain box otherwise. */
const schemaForElement = (element: unknown) => {
  const customAreaType = (element as { customAreaType?: unknown } | null)?.customAreaType;
  return typeof customAreaType === 'string' && isKnownCustomAreaType(customAreaType) ? KnownFreeElementSchema : UnknownFreeElementSchema;
};

/**
 * Validates known and unknown element types alike, but is typed as the known
 * union: code is written against the types it knows, and an unknown one only
 * ever passes through as data (drawn, moved and saved as a box).
 *
 * Each element is first checked against the one schema its type picks, so an
 * invalid element of a known type reports its own fields' errors rather than
 * failing as an unknown type. The union then parses it, and is what JSON
 * Schema and OpenAPI documents show.
 */
export const FloorPlanFreeElementSchema = z.preprocess((element, context) => {
  const result = schemaForElement(element).safeParse(element);
  if (!result.success) context.issues.push(...(result.error.issues as typeof context.issues));
  return element;
}, z.union([KnownFreeElementSchema, UnknownFreeElementSchema])) as unknown as typeof KnownFreeElementSchema;

// **************************************************************************************
// Main Floor Plan Room Schema
// **************************************************************************************
export const FloorPlanRoomSchema = keepingUnknownFields(zodLess({
  width: number,
  length: number,
  height: optionalNumber.describe('height of the ceiling'),
  floorType: optionalString.describe('default floor material for the whole room'),

  magneticNorthAngle: optionalNumber.describe(
    "Bearing of magnetic north: radians, clockwise from the back edge's outward direction (the room drawn back-edge-up). Copied from RawFloorPlan.magneticNorthAngle when the room was scanned.",
  ),
  magneticNorthSpreadDeg: optionalNumber.describe(
    'Circular standard deviation (degrees) of the compass samples behind magneticNorthAngle; ≲5° = consistent, ≳15° = treat as a hint.',
  ),

  /** The four primary-rectangle corner chamfers (clockwise from back-left). */
  backLeftCorner: FloorPlanCornerSchema.optional(),
  backRightCorner: FloorPlanCornerSchema.optional(),
  frontRightCorner: FloorPlanCornerSchema.optional(),
  frontLeftCorner: FloorPlanCornerSchema.optional(),

  leftEdge: FloorPlanPrimaryEdgeSchema.optional(),
  backEdge: FloorPlanPrimaryEdgeSchema.optional(),
  rightEdge: FloorPlanPrimaryEdgeSchema.optional(),
  frontEdge: FloorPlanPrimaryEdgeSchema.optional(),

  freeElements: z.array(FloorPlanFreeElementSchema).optional().describe(
    "interior rectangles, resolved by painter's algorithm in a fixed kind order (flooring, then counters, then walls) and within a kind by array order (later = on top). See `scene.ts`.",
  ),

  // Placement in the whole-property floor plan (the assembler). Everything
  // above stays in room-local coordinates; these five fields say where the
  // room as a whole sits. All optional: a room with no x/y has not been placed.
  level: optionalNumber.describe('Building level the room is on: 0 = basement, 1 = first floor, 2 = second floor, and so on. Undefined === 1.'),
  x: optionalNumber.describe(
    "Whole-property plan position of the room's local origin (the primary rectangle's back-left corner), in feet. Undefined === not yet placed.",
  ),
  y: optionalNumber.describe(
    "Whole-property plan position of the room's local origin (the primary rectangle's back-left corner), in feet. Undefined === not yet placed.",
  ),
  z: optionalNumber.describe("Elevation of the room's floor in the whole-property plan, in feet. Undefined === 0."),
  rotation: optionalNumber.describe(
    "Whole-property plan rotation about the primary rectangle's center, in DEGREES (same convention as free elements). Undefined === 0.",
  ),
}));
/**
 * Declared as an explicit interface (not inferred from the schema): the inferred
 * type sits at TypeScript's instantiation-depth limit, so indexing the room by an
 * `EdgeName`/`CornerName` union throws TS2589 all over the codebase. Keep in sync
 * with `FloorPlanRoomSchema` above.
 */
export interface FloorPlanRoom {
  width: number;
  length: number;
  /** Height of the ceiling. */
  height?: number;
  /** Default floor material for the whole room. */
  floorType?: string;
  /** Bearing of magnetic north: radians, CW from the back edge's outward direction. */
  magneticNorthAngle?: number;
  /** Circular std-dev (degrees) of the compass samples behind magneticNorthAngle. */
  magneticNorthSpreadDeg?: number;

  backLeftCorner?: FloorPlanCorner;
  backRightCorner?: FloorPlanCorner;
  frontRightCorner?: FloorPlanCorner;
  frontLeftCorner?: FloorPlanCorner;

  leftEdge?: FloorPlanPrimaryEdge;
  backEdge?: FloorPlanPrimaryEdge;
  rightEdge?: FloorPlanPrimaryEdge;
  frontEdge?: FloorPlanPrimaryEdge;

  freeElements?: FloorPlanFreeElement[];

  /** Building level (0 = basement, 1 = first floor, ...). Undefined === 1. */
  level?: number;
  /** Whole-property plan position of the room's local origin, feet. Undefined === not yet placed. */
  x?: number;
  y?: number;
  /** Elevation of the room's floor in the whole-property plan, feet. Undefined === 0. */
  z?: number;
  /** Whole-property plan rotation about the primary rectangle's center, degrees. Undefined === 0. */
  rotation?: number;
}

/** @deprecated Use FloorPlanRoom instead */
export type RoomFloorPlan = FloorPlanRoom;

/** @deprecated Use FloorPlanRoomSchema instead */
export const RoomFloorPlanSchema = FloorPlanRoomSchema;

/** @deprecated Use FloorPlanPrimaryEdge instead */
export type FloorPlanEdge = FloorPlanPrimaryEdge;

/** @deprecated Use FloorPlanPrimaryEdgeSchema instead */
export const FloorPlanEdgeSchema = FloorPlanPrimaryEdgeSchema;
